import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserCog, RefreshCw, Search } from "lucide-react";

/**
 * Admin panel showing:
 *  - Recent impersonation sessions (who acted as whom, when, from where)
 *  - Every mutating action (POST/PUT/PATCH/DELETE) performed by an admin while
 *    impersonated — captured by the backend HTTP middleware.
 *
 * Read-only. Data comes from:
 *   GET /api/admin/impersonation-audit
 *   GET /api/admin/impersonation-actions
 */
function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function methodColor(m) {
  switch ((m || "").toUpperCase()) {
    case "POST":   return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "PUT":
    case "PATCH":  return "bg-sky-100 text-sky-800 border-sky-200";
    case "DELETE": return "bg-rose-100 text-rose-800 border-rose-200";
    default:       return "bg-slate-100 text-slate-700 border-slate-200";
  }
}

function statusColor(code) {
  const c = Number(code) || 0;
  if (c >= 200 && c < 300) return "text-emerald-700";
  if (c >= 300 && c < 400) return "text-sky-700";
  if (c >= 400 && c < 500) return "text-amber-700";
  if (c >= 500)            return "text-rose-700";
  return "text-slate-600";
}

export default function AdminImpersonationPanel() {
  const [sessions, setSessions] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/impersonation-audit?limit=200").then((r) => setSessions(r.data || [])).catch(() => setSessions([])),
      api.get("/admin/impersonation-actions?limit=500").then((r) => setActions(r.data || [])).catch(() => setActions([])),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filteredActions = useMemo(() => {
    const q1 = (q || "").toLowerCase().trim();
    if (!q1) return actions;
    return actions.filter((a) =>
      [a.admin_email, a.target_email, a.target_name, a.path, a.method]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q1))
    );
  }, [q, actions]);

  return (
    <div className="space-y-6" data-testid="admin-impersonation-panel">
      {/* Sessions */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <UserCog className="text-orange-800" size={18} />
            <div className="font-display font-bold text-lg">Impersonation sessions</div>
            <Badge variant="outline" className="text-[10px]">{sessions.length}</Badge>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="rounded-full gap-1" data-testid="impersonation-refresh-btn">
            <RefreshCw size={12} /> Refresh
          </Button>
        </div>
        {loading ? (
          <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">No impersonation sessions yet. Click "Act as this Owner" on any owner in the Users tab to start one.</div>
        ) : (
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="py-2 px-3">Started</th>
                  <th className="py-2 px-3">Admin</th>
                  <th className="py-2 px-3">Acted as</th>
                  <th className="py-2 px-3">Role</th>
                  <th className="py-2 px-3">IP</th>
                  <th className="py-2 px-3">Expires</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} data-testid={`impersonation-session-${s.id}`} className="border-t border-slate-100 align-top">
                    <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{fmt(s.started_at)}</td>
                    <td className="py-2 px-3">
                      <div className="font-semibold text-slate-800 break-all">{s.admin_email || "—"}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{s.admin_id}</div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="font-semibold text-orange-800 break-all">{s.target_email || "—"}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{s.target_id}</div>
                    </td>
                    <td className="py-2 px-3"><Badge variant="outline" className="capitalize">{s.target_role}</Badge></td>
                    <td className="py-2 px-3 text-slate-500 text-xs">{s.ip || "—"}</td>
                    <td className="py-2 px-3 text-slate-500 text-xs whitespace-nowrap">{fmt(s.expires_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="font-display font-bold text-lg">Actions taken while impersonated</div>
            <Badge variant="outline" className="text-[10px]">{filteredActions.length}{q ? ` / ${actions.length}` : ""}</Badge>
          </div>
          <div className="relative min-w-[220px] flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by email, path, method…"
              className="pl-8 h-9 rounded-full"
              data-testid="impersonation-actions-search"
            />
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mb-3">
          Only mutating requests (POST / PUT / PATCH / DELETE) are recorded. Read-only browsing (GET) is not logged.
        </div>
        {loading ? (
          <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
        ) : filteredActions.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">
            {actions.length === 0
              ? "No actions taken during impersonation yet."
              : "No actions match your filter."}
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="py-2 px-3">When</th>
                  <th className="py-2 px-3">By admin</th>
                  <th className="py-2 px-3">On behalf of</th>
                  <th className="py-2 px-3">Action</th>
                  <th className="py-2 px-3 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredActions.map((a) => (
                  <tr key={a.id} data-testid={`impersonation-action-${a.id}`} className="border-t border-slate-100 align-top">
                    <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{fmt(a.at)}</td>
                    <td className="py-2 px-3">
                      <div className="font-semibold text-slate-800 break-all">{a.admin_email || "—"}</div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="font-semibold text-orange-800 break-all">
                        {a.target_name || a.target_email || "—"}
                      </div>
                      <div className="text-[10px] text-slate-500 capitalize">{a.target_role}</div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-black tracking-wider ${methodColor(a.method)}`}>
                          {a.method}
                        </span>
                        <span className="font-mono text-xs text-slate-700 break-all">{a.path}</span>
                      </div>
                      {a.query && <div className="text-[10px] text-slate-400 font-mono break-all mt-0.5">?{a.query}</div>}
                    </td>
                    <td className={`py-2 px-3 text-right font-bold ${statusColor(a.status_code)}`}>{a.status_code}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
